import { HuaweiBridgeClient } from './huawei-bridge-client';
/**
 * GeolocationHuawei implements the same GeolocationPlugin interface as
 * @capacitor/geolocation, routing calls through the Huawei JS Bridge.
 *
 * getCurrentPosition / clearWatch / checkPermissions / requestPermissions
 * use the standard request-response bridge.
 *
 * watchPosition registers a global push callback so the native side can
 * continuously deliver position updates via
 *   window.aigensGeolocationWatch(id, position)
 *   window.aigensGeolocationWatchError(id, error)
 */
export class GeolocationHuawei {
    constructor() {
        this.watchCallbacks = new Map();
        /** Buffered positions for watchIds whose callback isn't registered yet (race safety) */
        this.pendingPositions = new Map();
        this.initialized = false;
        this.bridge = HuaweiBridgeClient.getInstance();
    }
    /**
     * Lazily register global watch push handlers on first watchPosition call.
     */
    ensureWatchInitialized() {
        if (this.initialized)
            return;
        window.aigensGeolocationWatch = (id, position) => {
            const cb = this.watchCallbacks.get(id);
            if (cb) {
                cb(position);
            }
            else {
                // Buffer latest position in case callback isn't registered yet
                this.pendingPositions.set(id, position);
            }
        };
        window.aigensGeolocationWatchError = (id, error) => {
            const cb = this.watchCallbacks.get(id);
            if (cb) {
                cb(null, new Error(error));
            }
        };
        this.initialized = true;
    }
    async getCurrentPosition(options) {
        return this.bridge.call('getCurrentPosition', options || {});
    }
    async watchPosition(options, callback) {
        this.ensureWatchInitialized();
        // Request a watch from native; resolves with { watchId: string }
        const result = await this.bridge.call('watchPosition', options || {});
        const watchId = result.watchId || result.id;
        if (!watchId) {
            throw new Error('watchPosition: native did not return a watchId');
        }
        this.watchCallbacks.set(watchId, callback);
        // Flush any buffered position that arrived before callback registration
        const pending = this.pendingPositions.get(watchId);
        if (pending) {
            this.pendingPositions.delete(watchId);
            callback(pending);
        }
        return watchId;
    }
    async clearWatch(options) {
        const watchId = options.id;
        // Remove local callback and any pending positions before asking native to stop
        this.watchCallbacks.delete(watchId);
        this.pendingPositions.delete(watchId);
        await this.bridge.call('clearWatch', { id: watchId });
    }
    async checkPermissions() {
        return this.bridge.call('checkGeolocationPermissions');
    }
    async requestPermissions(permissions) {
        return this.bridge.call('requestGeolocationPermissions', permissions || {});
    }
}
//# sourceMappingURL=geolocation.js.map